import { PerspectiveCamera, Vector3 } from "three";
import { Canvas3d } from "../display3d/Canvas3d.js";
import { VOMarker } from "../model/DataStructure.js";
import { Marker } from "./Marker.js";

export interface MarkerUpdateInfo {
    x: number;
    y: number;
    cameraDistance: number;
    zoom: number;
    zIndex: number;
}

export interface MarkerLayerOptions {
    container: HTMLElement;
    canvas: Canvas3d;
    markers: VOMarker[];
    onSelect: (m: VOMarker) => void;
    onDeselect: (m: VOMarker) => void;
}

export class MarkerLayer {

    public onSelect = e5.Signal.create<[VOMarker]>();
    public onDeselect = e5.Signal.create<[VOMarker]>();

    private _element: HTMLElement;
    private _canvas: Canvas3d;
    private _camera: PerspectiveCamera;
    private _markers: Marker[] = [];
    private _marker: VOMarker = null;

    constructor(options: MarkerLayerOptions) {
        this._element = document.createElement("div");
        this._element.className = "awi-marker-layer";

        this._canvas = options.canvas;
        this._camera = options.canvas.camera;

        if (typeof options.onSelect === "function") {
            this.onSelect.add(options.onSelect);
        }

        if (typeof options.onDeselect === "function") {
            this.onDeselect.add(options.onDeselect);
        }

        options.markers.forEach((m, index) => {
            this.addMarker(m);
        });

        if (options.container) {
            options.container.appendChild(this._element);
        }

        this._canvas.onAfterUpdate.add(() => this.update());
    }

    public get visible(): boolean {
        return !this._element.classList.contains("awi-marker-layer--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("awi-marker-layer--hidden", !value);
    }

    public get disabled(): boolean {
        return this._element.classList.contains("awi-marker-layer--disabled");
    }

    public set disabled(value: boolean) {
        this._element.classList.toggle("awi-marker-layer--disabled", value);
    }

    public get marker(): VOMarker {
        return this._marker;
    }

    public set marker(value: VOMarker) {
        if (this._marker?.id == value?.id) {
            return;
        }

        var m = this.getMarker(this._marker);
        if (m) {
            m.selected = false;
        }

        this._marker = value;


        var m = this.getMarker(this._marker);
        if (m) {
            m.selected = true;
        }

        // this.visible = Boolean(value);
    }

    private getMarker(m: VOMarker): Marker {
        if (!m) {
            return null;
        }
        for (let ma of this._markers) {
            if (ma.marker.id == m.id) {
                return ma;
            }
        }
        return null;
    }

    private addMarker(m: VOMarker): void {
        let marker = new Marker(
            this._element,
            {
                marker: m
            });
        marker.onClick.add((marker) => {
            if (marker.selected) {
                this.onDeselect.dispatch(m);
            } else {
                this.onSelect.dispatch(m);
            }
        });
        this._markers.push(marker);
    }

    private update(): void {

        this._camera.updateMatrixWorld();

        var info: MarkerUpdateInfo = {
            cameraDistance: this._camera.position.length(),
            x: 0,
            y: 0,
            zoom: this._camera.zoom,
            zIndex: 0
        }

        /**
         * Sort by camera distance.
         */
        for (var m of this._markers) {
            m.cameraDistance = m.position.distanceTo(this._camera.position);
        }
        this._markers.sort((a, b) => {
            return b.cameraDistance - a.cameraDistance;
        });

        var zIndex = 1;
        const width = this._element.offsetWidth;
        const height = this._element.offsetHeight;
        for (var m of this._markers) {

            m.position.set(
                m.marker.target_x,
                m.marker.target_y,
                m.marker.target_z
            );
            var pos = m.position.clone().project(this._camera);
            info.x = (pos.x + 1) * width * 0.5;
            info.y = (-pos.y + 1) * height * 0.5;

            info.zIndex = e5.Calc.clamp(m.zIndexOffset + zIndex++, 0, 8192);

            m.update(info);
        }
    }
}